import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  SignalIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

interface TwitchConnection {
  id: string;
  userId: string;
  twitchUserId: string;
  twitchUsername: string;
  displayName: string;
  profileImageUrl?: string;
  isLive: boolean;
  lastSync: Date;
}

interface TwitchStream {
  id: string;
  title: string;
  gameName: string;
  viewerCount: number;
  thumbnailUrl: string;
  startedAt: string;
}

interface TwitchVOD {
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  createdAt: string;
  viewCount: number;
  duration: string;
}

const TwitchIntegrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [connection, setConnection] = useState<TwitchConnection | null>(null);
  const [stream, setStream] = useState<TwitchStream | null>(null);
  const [vods, setVods] = useState<TwitchVOD[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchConnection();
  }, []);

  const fetchConnection = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/platform-integrations/twitch/status');
      setConnection(response.data.connection);
      
      if (response.data.connection) {
        checkLiveStatus();
        fetchVODs();
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Error fetching Twitch connection:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkLiveStatus = async () => {
    try {
      setChecking(true);
      const response = await axios.get('/api/platform-integrations/twitch/live-status');
      setStream(response.data.stream);
    } catch (error) {
      console.error('Error checking live status:', error);
    } finally {
      setChecking(false);
    }
  };

  const fetchVODs = async () => {
    try {
      const response = await axios.get('/api/platform-integrations/twitch/vods?limit=10');
      setVods(response.data.vods);
    } catch (error) {
      console.error('Error fetching VODs:', error);
    }
  };

  const connectTwitch = async () => {
    try {
      const response = await axios.get('/api/platform-integrations/twitch/auth-url');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting Twitch:', error);
    }
  };

  const disconnectTwitch = async () => {
    if (!confirm('Are you sure you want to disconnect Twitch?')) return;

    try {
      await axios.delete('/api/platform-integrations/twitch/disconnect');
      setConnection(null);
      setStream(null);
      setVods([]);
    } catch (error) {
      console.error('Error disconnecting Twitch:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/integrations')}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Integrations
      </button>

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <span className="text-5xl">🎥</span>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Twitch Integration</h1>
          <p className="text-gray-600 mt-1">
            Monitor live streams and import VODs from your Twitch channel
          </p>
        </div>
      </div>

      {!connection ? (
        /* Not Connected State */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Connect Your Twitch Channel</h2>
          <p className="text-gray-600 mb-6">
            Grant access to your Twitch channel to monitor streams and import VODs.
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Live Stream Monitoring</h3>
                <p className="text-sm text-gray-600">
                  Check if your channel is currently live
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">VOD Management</h3>
                <p className="text-sm text-gray-600">
                  Fetch and import past broadcasts
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Stream Statistics</h3>
                <p className="text-sm text-gray-600">
                  Track viewer counts and stream metrics
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={connectTwitch}
            className="w-full sm:w-auto px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            Connect Twitch Channel
          </button>
        </div>
      ) : (
        /* Connected State */
        <div className="space-y-6">
          {/* Connection Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Channel Connected</h2>
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="text-sm font-medium">Active</span>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-6">
              {connection.profileImageUrl && (
                <img
                  src={connection.profileImageUrl}
                  alt="Twitch Profile"
                  className="h-16 w-16 rounded-full"
                />
              )}
              <div>
                <p className="font-medium text-gray-900 text-lg">{connection.displayName}</p>
                <p className="text-sm text-gray-500">@{connection.twitchUsername}</p>
                <p className="text-xs text-gray-400 mt-1">User ID: {connection.twitchUserId}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={checkLiveStatus}
                disabled={checking}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
                {checking ? 'Checking...' : 'Check Live Status'}
              </button>
              <button
                onClick={disconnectTwitch}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
              >
                Disconnect
              </button>
            </div>
          </div>

          {/* Live Status */}
          <div className={`rounded-lg shadow-sm border p-6 ${
            stream ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <SignalIcon className={`h-6 w-6 ${stream ? 'text-red-600' : 'text-gray-400'}`} />
              <h2 className="text-xl font-semibold text-gray-900">
                {stream ? 'Currently Live!' : 'Offline'}
              </h2>
            </div>

            {stream ? (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Stream Title</p>
                  <p className="font-medium text-gray-900">{stream.title}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Game/Category</p>
                    <p className="font-medium text-gray-900">{stream.gameName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Viewers</p>
                    <p className="font-medium text-gray-900">{stream.viewerCount.toLocaleString()}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Started At</p>
                  <p className="text-sm text-gray-600">
                    {new Date(stream.startedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-600">No active stream detected</p>
            )}
          </div>

          {/* Recent VODs */}
          {vods.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent VODs</h2>
              <div className="grid grid-cols-1 gap-4">
                {vods.map((vod) => (
                  <div
                    key={vod.id}
                    className="flex gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <img
                        src={vod.thumbnailUrl.replace('%{width}', '320').replace('%{height}', '180')}
                        alt={vod.title}
                        className="w-40 h-24 object-cover rounded"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{vod.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                        {vod.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{vod.viewCount.toLocaleString()} views</span>
                        <span>{vod.duration}</span>
                        <span>{new Date(vod.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 self-center"
                    >
                      <VideoCameraIcon className="h-4 w-4" />
                      Import
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TwitchIntegrationPage;
